import {
  PRIORITY_INBOX_DEFAULT_STATUS,
  PRIORITY_INBOX_KINDS,
  PRIORITY_INBOX_STATUSES,
  type PriorityInboxKind,
  type PriorityInboxStatus,
} from "@circle-react/types/PriorityInboxThread";
import { z } from "zod";

// One flag per category (`?dms=1&moderation=1`). A joined string would be
// percent-encoded by URLSearchParams (`,` -> `%2C`), and an array serializes
// as repeated params. Absent flags mean every category is included.
const kindFlagSchema = z.literal(1).optional().catch(undefined);

const kindFlagShape = {
  ai: kindFlagSchema,
  chat_threads: kindFlagSchema,
  course_comments: kindFlagSchema,
  dms: kindFlagSchema,
  moderation: kindFlagSchema,
} as const satisfies Record<PriorityInboxKind, typeof kindFlagSchema>;

export const priorityInboxSearchSchema = z.object({
  ...kindFlagShape,
  // Absent means the default (unsolved) -- matches ThreadsController's own
  // default, so the URL only carries this param once it differs.
  status: z.enum(PRIORITY_INBOX_STATUSES).optional().catch(undefined),
});

export type PriorityInboxSearch = z.infer<typeof priorityInboxSearchSchema>;

export const parseStatusParam = (
  status: PriorityInboxStatus | undefined,
): PriorityInboxStatus => status ?? PRIORITY_INBOX_DEFAULT_STATUS;

export const serializeStatusParam = (
  status: PriorityInboxStatus,
): PriorityInboxStatus | undefined =>
  status === PRIORITY_INBOX_DEFAULT_STATUS ? undefined : status;

// Stable identity for the default "every category" state, which is omitted
// from the URL. A fresh array here would change identity on every parse.
const ALL_PRIORITY_INBOX_KINDS: PriorityInboxKind[] = [...PRIORITY_INBOX_KINDS];

export const parseKindsParam = (
  search: PriorityInboxSearch,
): PriorityInboxKind[] => {
  const kinds = PRIORITY_INBOX_KINDS.filter(kind => search[kind] === 1);
  return kinds.length > 0 ? kinds : ALL_PRIORITY_INBOX_KINDS;
};

export const serializeKindsParam = (
  kinds: PriorityInboxKind[],
): Partial<Record<PriorityInboxKind, 1 | undefined>> => {
  if (kinds.length === PRIORITY_INBOX_KINDS.length) {
    return Object.fromEntries(
      PRIORITY_INBOX_KINDS.map(kind => [kind, undefined]),
    );
  }

  return Object.fromEntries(
    PRIORITY_INBOX_KINDS.map(kind => [
      kind,
      kinds.includes(kind) ? 1 : undefined,
    ]),
  );
};
